<template>
    <div>
        <div class="card">
            <DataTable
                v-model:selection="selectedAttributes"
                :value="allAttributes"
                :lazy="true"
                paginator
                :rows="10"
                ref="dt"
                dataKey="id"
                :loading="loading"
                @page="onPage"
                :totalRecords="totalAttributes"
                responsiveLayout="scroll"
                :rowsPerPageOptions="[5, 10, 20, 50]"
                paginatorTemplate="CurrentPageReport JumpToPageInput FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries :: Page No:"
            >
                <template #header>
                    <div class="flex justify-content-between flex-column sm:flex-row">
                        <div class="flex">
                            <!-- only allow god users AND 
                                 visible only when attributes are not tagged
                            -->
                            <div v-if="isGodUser && params.tagged === false">
                                <Button
                                    :label="sharedType === SharedTypes.COMPANY ? 'Export to Shared' : 'Import to Company'"
                                    class="p-button-warning mr-2"
                                    @click="confirmTransfer(selectedAttributes, sharedType === SharedTypes.COMPANY, attributeStore.transferAttributes)"
                                />
                            </div>
                        </div>
                    </div>
                </template>
                <template #empty> No attributes available. </template>
                <template #loading> Loading Attributes. Please wait. </template>
                <Column selectionMode="multiple" headerStyle="width: 6%" />
                <Column field="id" header="ID" style="width: 6%" />
                <Column field="label" header="LABEL" style="width: 18%; overflow: hidden">
                    <template #body="slotProps">
                        {{ slotProps.data.label ? slotProps.data.label : 'No Label' }}
                    </template>
                </Column>
                <Column field="code" header="CODE" style="width: 12%; overflow: hidden" />
                <Column field="version" header="VERSION" style="width: 8%" />
                <Column field="default" header="DEFAULT" style="width: 10%">
                    <template #body="slotProps">
                        <Tag :severity="slotProps.data.default == 1 ? 'info' : 'danger'" :value="slotProps.data.default == 1 ? 'True' : 'False'" rounded />
                    </template>
                </Column>
                <Column field="type_string" header="TYPE" style="width: 10%" />
                <Column field="created_at" header="CREATED AT" style="width: 12%" />
                <Column field="actions" header="ACTIONS" style="width: 18%">
                    <template #body="slotProps">
                        <Button icon="pi pi-eye" class="p-button-rounded p-button-info mr-2" @click="viewDetails(slotProps.data.id)" v-tooltip="'View Attribute Details'" />
                        <Button v-if="checkUserPermission(UserPermissions.WRITE_ASSESSMENT_ATTRIBUTES)" icon="pi pi-pencil" class="p-button-rounded p-button-success mr-2" @click="editDetails(slotProps.data.id)" v-tooltip="'Edit Attribute'" />
                        <!-- check permissions for deletion of normal attributes as well as user profile attributes -->
                        <Button
                            v-if="(checkUserPermission(UserPermissions.DELETE_ASSESSMENT_ATTRIBUTES) && !slotProps.data.user_profile) || (checkUserPermission(UserPermissions.DELETE_USER_PROFILE_ATTRIBUTES) && slotProps.data.user_profile)"
                            icon="pi pi-trash"
                            class="p-button-rounded p-button-danger mr-2"
                            @click="confirmRequest(slotProps.data, attributeStore.deleteAttribute)"
                            v-tooltip="'Delete Attribute'"
                            :disabled="!slotProps.data.deletable"
                        />
                        <Button
                            v-if="checkUserPermission(UserPermissions.WRITE_ASSESSMENT_ATTRIBUTES) && slotProps.data.default != 1"
                            icon="pi pi-check"
                            class="p-button-rounded p-button-help"
                            @click="confirmRequest(slotProps.data, attributeStore.setDefaultAttribute, 'default')"
                            v-tooltip="'Set Default'"
                        />
                    </template>
                </Column>
            </DataTable>
        </div>
        <!-- Attribute edit form -->
        <attribute-form v-if="editDialogForm" @hide-dialog="editDialogForm = false" />
        <!-- attribute details -->
        <attribute-details v-if="detailsDialog" :attribute="attributeData" @hide-dialog="detailsDialog = false" />
    </div>
</template>

<script setup>
import AttributeForm from './AttributeForm.vue';
import AttributeDetails from './AttributeDetails.vue';

import { ref, computed, onMounted, watch } from 'vue';
import { storeToRefs } from 'pinia';

import { useAttributeStore } from '../store';
import useUserComposable from '@/composables/UserComposable';
import { useConfirmComposable } from '@/composables/ConfirmComposable';

import { UserPermissions } from '../../../../../enums';
import { SharedTypes } from '../../../enum';

const attributeStore = useAttributeStore();
const { params, allAttributes, loading, totalAttributes } = storeToRefs(attributeStore);

const { isGodUser, checkUserPermission } = useUserComposable();
const { confirmRequest, confirmTransfer } = useConfirmComposable();

const editDialogForm = ref(false);
const detailsDialog = ref(false);
const attributeData = ref(null);
const selectedAttributes = ref([]);

const sharedType = computed(() => {
    return attributeStore.params.shared;
});

watch(sharedType, () => {
    selectedAttributes.value = null;
});

onMounted(() => {
    loadAttributes();
    loadSharedTaggedAttributes();
});

const loadAttributes = async () => {
    await attributeStore.fetchAllAttributes(true);
};

const loadSharedTaggedAttributes = async () => {
    await attributeStore.fetchAllSharedTaggedAttributes();
};

const viewDetails = (attributeId) => {
    attributeData.value = allAttributes.value?.find((attribute) => attribute.id === attributeId);
    detailsDialog.value = true;
};

const editDetails = (attributeId) => {
    editDialogForm.value = true;
    attributeStore.editAttribute(attributeId);
};

const onPage = async (event) => {
    params.value.page = event.page + 1;
    params.value.page_size = event.rows;
    // The params changes are automatically triggered by AttributeFilters component
    //  so we dont need to loadAttributes here
};
</script>
